import { defineAsyncComponent } from 'vue';
import { reportToSentry } from '~/ci/utils';
import { sanitize } from '~/lib/dompurify';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { setFaviconOverlay } from '~/lib/utils/favicon';
import { TYPENAME_CI_PIPELINE } from '~/graphql_shared/constants';
import MrPipelineUpdated from '../subscriptions/mr_pipeline_updated.subscription.graphql';
import ArtifactsApp from './artifacts_list_app.vue';
import DeploymentList from './deployment/deployment_list.vue';
import MrWidgetContainer from './mr_widget_container.vue';
import MrWidgetPipeline from './mr_widget_pipeline.vue';
/**
 * Renders the pipeline and related deployments from the store.
 *
 * | Props         | Description
 * |---------------|-------------
 * | `mr`          | This is the mr_widget store
 * | `isPostMerge` | If true, show the "post merge" pipeline and deployments
 */ export default {
    name: 'MrWidgetPipelineContainer',
    components: {
        ArtifactsApp,
        DeploymentList,
        MrWidgetContainer,
        MrWidgetPipeline,
        MergeTrainPositionIndicator: defineAsyncComponent(()=>import('ee_component/vue_merge_request_widget/components/merge_train_position_indicator.vue'))
    },
    props: {
        mr: {
            type: Object,
            required: true
        },
        isPostMerge: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    apollo: {
        $subscribe: {
            // eslint-disable-next-line @gitlab/vue-no-undef-apollo-properties
            pipelineStatuses: {
                query: MrPipelineUpdated,
                variables () {
                    return {
                        // crucial to use the computed property `this.pipeline` for variables
                        // since it handles merge pipelines and normal pipelines
                        pipelineId: convertToGraphQLId(TYPENAME_CI_PIPELINE, this.pipeline?.id)
                    };
                },
                skip () {
                    return !this.pipeline?.id;
                },
                result ({ data }) {
                    if (!data.ciPipelineStatusUpdated) return;
                    this.mr.setPipelineStatusData(data.ciPipelineStatusUpdated, this.isPostMerge);
                    const favicon = data.ciPipelineStatusUpdated.detailedStatus?.favicon;
                    if (favicon && !this.isPostMerge && !this.mr.isMergedState) setFaviconOverlay(favicon);
                },
                error (err) {
                    reportToSentry(this.$options.name, err);
                }
            }
        }
    },
    computed: {
        branch () {
            return this.isPostMerge ? this.mr.targetBranch : this.mr.sourceBranch;
        },
        branchLink () {
            return this.isPostMerge ? sanitize(this.mr.targetBranch) : this.mr.sourceBranchLink;
        },
        deployments () {
            return this.isPostMerge ? this.mr.postMergeDeployments : this.mr.deployments;
        },
        deploymentClass () {
            return this.isPostMerge ? 'js-post-deployment' : 'js-pre-deployment';
        },
        hasDeploymentMetrics () {
            return this.isPostMerge;
        },
        pipeline () {
            return this.isPostMerge ? this.mr.mergePipeline : this.mr.pipeline;
        },
        ciStatus () {
            return this.isPostMerge ? this.mr?.mergePipeline?.details?.status?.text : this.mr.ciStatus;
        }
    }
};
